import { createContext, useContext, useEffect, useState } from "react";

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
];

export const translations = {
  en: {
    nav: { modes: "Modes", how: "How it works", resenha: "Resenha", sports: "Sports", claim: "Claim @handle" },
    hero: {
      overline: "[ A social network for wins, rankings & collections ]",
      l1: "Own the", l2: "Score.", l3: "Pin the Win.",
      subPre: "Solo or with your squad. The real-life sticker album for every kickaround, poker night and pickup game.",
      subStrong: "If it happened, PinWin.",
      ctaPrimary: "Pin your first win", ctaSecondary: "How it works",
      stats: [
        { k: "∞", v: "pins per life" },
        { k: "5", v: "tabs, no noise" },
        { k: "100%", v: "free, no ads" },
        { k: "3", v: "native languages" },
      ],
    },
    two: {
      overline: "[ Two sides, one album ]", headA: "Solo or with", headB: "your squad.",
      modes: [
        { tag: "01 / Collective", desc: "Create a crew, dispute events, and let the ranking decide who talks and who stays quiet at the next meetup.", points: ["Auto ranking with W / L & streaks", "Activity feed of every pin", "Resenha chat with the AI Bot Judge", "Invite by link or code"] },
        { tag: "02 / Individual", desc: "Catalogue your passions and personal milestones. A public showcase of everything you collected, conquered or crossed off.", points: ["Craft beers, countries, coins, books", "Trophies unlocked by performance", "Confirmed wins history", "Public or private collections"] },
      ],
    },
    how: {
      overline: "[ How it works ]", headA: "Three steps", headB: "to history.",
      note: "Drop a pin. Squad confirms. Ranking updates. In 15 seconds it's on record.",
      steps: [
        { title: "Drop a Pin", desc: "Register the result. Pick the sport, select the squad, drop the score. Solo pins go straight to your vault as personal wins." },
        { title: "Squad Confirms", desc: "The whole squad gets pinged to confirm or contest. Majority rules — 3 of 5 confirm and it's official. Contested pins get flagged." },
        { title: "Ranking Updates", desc: "The table moves. Wins climb, losses drop, streaks unlock trophies — and the Bot Judge narrates the whole thing with zero mercy." },
      ],
    },
    tabs: {
      overline: "[ The app ]", headA: "Four tabs.", headB: "No noise.",
      items: [
        { tag: "The feed", desc: "Everything from your squads and friends. Face-to-face cards, scores, status and the Bot Judge's roast." },
        { tag: "Your crews", desc: "Create groups, invite by link or code, track members and disputes." },
        { tag: "The trigger", desc: "Tap + to log a result. Sport, squad, score. Done in 15s." },
        { tag: "Your album", desc: "Collections, trophies and confirmed wins — your real-life sticker album." },
        { tag: "Your network", desc: "Add by search, accept requests, and watch their activity ripple into your Arena." },
      ],
    },
    resenha: {
      overline: "[ The Resenha Engine ]", headA: "Trash talk,", headB: "automated.",
      para: "Every squad ships with the Bot Judge — an AI narrator that reads each dispute and fires off a personalised roast. It celebrates comebacks, exposes losing streaks, and keeps the group alive between matches.",
      bullets: ["Narrates scores — \"Léo wins 7×4. Mari drops to #2.\"", "Detects patterns — blowouts, nail-biters, comebacks, underdogs", "Roasts the bottom — \"Carlos has been last for 3 weeks. Just saying.\"", "Speaks PT · EN · ES natively"],
      live: "live", pinLabel: "Pin · confirmed",
      m1: "Léo wins 7×4. Mari drops to #2. Brutal.",
      m2pre: "That's ", m2strong: "5 in a row", m2post: " for Léo. Somebody stop this man. 🏆",
      m3: "Carlos has been last for 3 weeks straight. Just saying.",
    },
    sports: {
      overline: "[ Sports & mechanics ]", headA: "21 sports.", headB: "7 ways to score.",
      note: "Versus, sets, multiplayer, solo — new sports added by admin, no code required.",
    },
    manifesto: {
      overline: "[ Manifesto ]",
      chapters: [
        { lead: "The score", rest: "is more real than the photo." },
        { lead: "The ranking", rest: "is more real than the post." },
        { lead: "The resenha", rest: "is the best part of every game." },
      ],
      closing: "PINWIN is the real-life sticker album. Where the pelada de quarta gets an official ranking, where the churrasco truco gets an AI narrator, and where every game between friends becomes story, rivalry and resenha.",
    },
    waitlist: {
      overline: "[ Reserve your handle ]", headA: "Pin your", headB: "first win.",
      subPre: "Claim your ", subLink: "pinwin.social/@handle", subPost: " before someone in your squad does. Join the waitlist and be first when we drop.",
      handleLabel: "Handle", handlePlaceholder: "yourname",
      emailLabel: "Email", emailPlaceholder: "you@email.com",
      submit: "Reserve my handle", footnote: "100% free · no ads · no spam",
      available: "Available — grab it.", taken: "Already claimed. Try another.", invalid: "3–20 chars · a-z, 0-9, _ only.",
      successTitleSuffix: "secured",
      successPre: "You're on the waitlist. We'll email you the moment PINWIN goes live.", successMid: "",
      toastSuccess: (h, p) => `@${h} is yours!`,
      toastTaken: "That handle is already claimed. Try another.",
      toastInvalidHandle: "Handle must be 3–20 chars: letters, numbers, underscore.",
      toastInvalidEmail: "Enter a valid email.", toastGeneric: "Something went wrong. Try again.",
    },
    footer: {
      cta: "Claim your @handle →", tagPre: "If it happened,", tagStrong: "PinWin.",
      cols: [
        { title: "Product", links: ["Squads", "Vault", "Resenha Engine", "Sports"] },
        { title: "Company", links: ["Manifesto", "Careers", "Press", "Contact"] },
        { title: "Legal", links: ["Privacy", "Terms", "Cookies"] },
      ],
      copyright: "Own the Score. Pin the Win.",
    },
  },

  pt: {
    nav: { modes: "Modos", how: "Como funciona", resenha: "Resenha", sports: "Esportes", claim: "Reservar @handle" },
    hero: {
      overline: "[ A rede social de vitórias, rankings e coleções ]",
      l1: "Domine o", l2: "Placar.", l3: "Pin the Win.",
      subPre: "Solo ou com seu squad. O álbum de figurinhas da vida real pra cada pelada, poker de sexta e jogo de fim de semana.",
      subStrong: "Se aconteceu, PinWin.",
      ctaPrimary: "Registre sua vitória", ctaSecondary: "Como funciona",
      stats: [
        { k: "∞", v: "pins por vida" },
        { k: "5", v: "abas, zero ruído" },
        { k: "100%", v: "grátis, sem ads" },
        { k: "3", v: "idiomas nativos" },
      ],
    },
    two: {
      overline: "[ Dois lados, um álbum ]", headA: "Solo ou com", headB: "seu squad.",
      modes: [
        { tag: "01 / Coletivo", desc: "Crie um grupo, dispute eventos e deixe o ranking decidir quem fala e quem cala no próximo encontro.", points: ["Ranking automático com V / D e sequências", "Feed de atividade com todos os pins", "Chat Resenha com o Bot Judge (IA)", "Convite por link ou código"] },
        { tag: "02 / Individual", desc: "Catalogue suas paixões e marcos pessoais. Uma vitrine pública de tudo que você colecionou, conquistou ou riscou da lista.", points: ["Cervejas, países, moedas, livros", "Troféus desbloqueados por performance", "Histórico de vitórias confirmadas", "Coleções públicas ou privadas"] },
      ],
    },
    how: {
      overline: "[ Como funciona ]", headA: "Três passos", headB: "pra história.",
      note: "Registre o pin. O squad confirma. O ranking atualiza. Em 15 segundos tá oficial.",
      steps: [
        { title: "Registre o Pin", desc: "Registre o resultado. Escolha o esporte, selecione o squad, coloque o placar. Pins solo vão direto pro seu vault como conquistas." },
        { title: "Squad Confirma", desc: "O squad inteiro é notificado pra confirmar ou contestar. Maioria manda — 3 de 5 confirmam e é oficial. Contestados ficam marcados." },
        { title: "Ranking Atualiza", desc: "A tabela se move. Vitórias sobem, derrotas descem, sequências geram troféus — e o Bot Judge narra tudo sem dó." },
      ],
    },
    tabs: {
      overline: "[ O app ]", headA: "Quatro abas.", headB: "Zero ruído.",
      items: [
        { tag: "O feed", desc: "Tudo dos seus squads e amigos. Cards frente a frente, placares, status e a zoação do Bot Judge." },
        { tag: "Seus grupos", desc: "Crie grupos, convide por link ou código, acompanhe membros e disputas." },
        { tag: "O gatilho", desc: "Toque no + pra registrar um resultado. Esporte, squad, placar. Pronto em 15s." },
        { tag: "Seu álbum", desc: "Coleções, troféus e vitórias confirmadas — seu álbum de figurinhas da vida real." },
        { tag: "Sua rede", desc: "Adicione por busca, aceite pedidos e veja a atividade deles chegar na sua Arena." },
      ],
    },
    resenha: {
      overline: "[ O Resenha Engine ]", headA: "Zoação,", headB: "automatizada.",
      para: "Cada squad vem com o Bot Judge — um narrador com IA que analisa cada disputa e dispara uma zoação personalizada. Celebra viradas, expõe quem tá perdendo e mantém o grupo aceso entre as partidas.",
      bullets: ["Narra placares — \"Léo vence 7×4. Mari cai pro #2.\"", "Detecta padrões — goleadas, jogos apertados, viradas, azarões", "Puxa o pé — \"Carlos tá em último faz 3 semanas. Só avisando.\"", "Fala PT · EN · ES nativamente"],
      live: "ao vivo", pinLabel: "Pin · confirmado",
      m1: "Léo vence 7×4. Mari cai pro #2. Brutal.",
      m2pre: "São ", m2strong: "5 seguidas", m2post: " pro Léo. Alguém para esse cara. 🏆",
      m3: "Carlos tá em último faz 3 semanas seguidas. Só avisando.",
    },
    sports: {
      overline: "[ Esportes & mecânicas ]", headA: "21 esportes.", headB: "7 formas de pontuar.",
      note: "Versus, sets, multiplayer, solo — novos esportes adicionados pelo admin, sem código.",
    },
    manifesto: {
      overline: "[ Manifesto ]",
      chapters: [
        { lead: "O placar", rest: "é mais real que a foto." },
        { lead: "O ranking", rest: "é mais real que o post." },
        { lead: "A resenha", rest: "é a melhor parte de todo jogo." },
      ],
      closing: "PINWIN é o álbum de figurinhas da vida real. Onde a pelada de quarta tem ranking oficial, onde o truco do churrasco tem narrador com IA, e onde cada jogo entre amigos vira história, rivalidade e resenha.",
    },
    waitlist: {
      overline: "[ Reserve seu handle ]", headA: "Registre sua", headB: "primeira vitória.",
      subPre: "Garanta seu ", subLink: "pinwin.social/@handle", subPost: " antes que alguém do seu squad garanta. Entre na lista e seja o primeiro no lançamento.",
      handleLabel: "Handle", handlePlaceholder: "seunome",
      emailLabel: "Email", emailPlaceholder: "voce@email.com",
      submit: "Reservar meu handle", footnote: "100% grátis · sem ads · sem spam",
      available: "Disponível — pega logo.", taken: "Já foi reservado. Tenta outro.", invalid: "3–20 caracteres · a-z, 0-9, _ apenas.",
      successTitleSuffix: "garantido",
      successPre: "Você está na lista de espera. A gente te avisa por email assim que o PINWIN for lançado.", successMid: "",
      toastSuccess: (h, p) => `@${h} é seu!`,
      toastTaken: "Esse handle já foi reservado. Tenta outro.",
      toastInvalidHandle: "O handle deve ter 3–20 caracteres: letras, números, underscore.",
      toastInvalidEmail: "Digite um email válido.", toastGeneric: "Algo deu errado. Tenta de novo.",
    },
    footer: {
      cta: "Reserve seu @handle →", tagPre: "Se aconteceu,", tagStrong: "PinWin.",
      cols: [
        { title: "Produto", links: ["Squads", "Vault", "Resenha Engine", "Esportes"] },
        { title: "Empresa", links: ["Manifesto", "Carreiras", "Imprensa", "Contato"] },
        { title: "Legal", links: ["Privacidade", "Termos", "Cookies"] },
      ],
      copyright: "Domine o Placar. Pin the Win.",
    },
  },

  es: {
    nav: { modes: "Modos", how: "Cómo funciona", resenha: "Resenha", sports: "Deportes", claim: "Reservar @handle" },
    hero: {
      overline: "[ La red social de victorias, rankings y colecciones ]",
      l1: "Domina el", l2: "Marcador.", l3: "Pin the Win.",
      subPre: "Solo o con tu squad. El álbum de estampas de la vida real para cada picadito, noche de póker y partido de fin de semana.",
      subStrong: "Si pasó, PinWin.",
      ctaPrimary: "Registra tu victoria", ctaSecondary: "Cómo funciona",
      stats: [
        { k: "∞", v: "pins por vida" },
        { k: "5", v: "pestañas, sin ruido" },
        { k: "100%", v: "gratis, sin ads" },
        { k: "3", v: "idiomas nativos" },
      ],
    },
    two: {
      overline: "[ Dos lados, un álbum ]", headA: "Solo o con", headB: "tu squad.",
      modes: [
        { tag: "01 / Colectivo", desc: "Crea un grupo, disputa eventos y deja que el ranking decida quién habla y quién calla en el próximo encuentro.", points: ["Ranking automático con V / D y rachas", "Feed de actividad con todos los pins", "Chat Resenha con el Bot Judge (IA)", "Invita por link o código"] },
        { tag: "02 / Individual", desc: "Cataloga tus pasiones y logros personales. Una vitrina pública de todo lo que coleccionaste, conquistaste o tachaste.", points: ["Cervezas, países, monedas, libros", "Trofeos desbloqueados por rendimiento", "Historial de victorias confirmadas", "Colecciones públicas o privadas"] },
      ],
    },
    how: {
      overline: "[ Cómo funciona ]", headA: "Tres pasos", headB: "a la historia.",
      note: "Suelta el pin. El squad confirma. El ranking se actualiza. En 15 segundos queda oficial.",
      steps: [
        { title: "Suelta el Pin", desc: "Registra el resultado. Elige el deporte, selecciona el squad, pon el marcador. Los pins solo van directo a tu vault como logros." },
        { title: "El Squad Confirma", desc: "Todo el squad recibe aviso para confirmar o impugnar. Manda la mayoría — 3 de 5 confirman y es oficial. Los impugnados se marcan." },
        { title: "El Ranking Sube", desc: "La tabla se mueve. Las victorias suben, las derrotas bajan, las rachas dan trofeos — y el Bot Judge lo narra sin piedad." },
      ],
    },
    tabs: {
      overline: "[ La app ]", headA: "Cuatro pestañas.", headB: "Sin ruido.",
      items: [
        { tag: "El feed", desc: "Todo de tus squads y amigos. Tarjetas cara a cara, marcadores, estado y la burla del Bot Judge." },
        { tag: "Tus grupos", desc: "Crea grupos, invita por link o código, sigue miembros y disputas." },
        { tag: "El gatillo", desc: "Toca + para registrar un resultado. Deporte, squad, marcador. Listo en 15s." },
        { tag: "Tu álbum", desc: "Colecciones, trofeos y victorias confirmadas — tu álbum de estampas de la vida real." },
        { tag: "Tu red", desc: "Agrega por búsqueda, acepta solicitudes y ve su actividad llegar a tu Arena." },
      ],
    },
    resenha: {
      overline: "[ El Resenha Engine ]", headA: "Trash talk,", headB: "automatizado.",
      para: "Cada squad viene con el Bot Judge — un narrador con IA que lee cada disputa y suelta una burla personalizada. Celebra remontadas, expone rachas perdedoras y mantiene vivo al grupo entre partidos.",
      bullets: ["Narra marcadores — \"Léo gana 7×4. Mari baja al #2.\"", "Detecta patrones — goleadas, partidos cerrados, remontadas, tapados", "Se burla del último — \"Carlos lleva 3 semanas último. Solo aviso.\"", "Habla PT · EN · ES nativamente"],
      live: "en vivo", pinLabel: "Pin · confirmado",
      m1: "Léo gana 7×4. Mari baja al #2. Brutal.",
      m2pre: "Van ", m2strong: "5 seguidas", m2post: " para Léo. Que alguien lo pare. 🏆",
      m3: "Carlos lleva 3 semanas seguidas último. Solo aviso.",
    },
    sports: {
      overline: "[ Deportes y mecánicas ]", headA: "21 deportes.", headB: "7 formas de puntuar.",
      note: "Versus, sets, multijugador, solo — nuevos deportes añadidos por el admin, sin código.",
    },
    manifesto: {
      overline: "[ Manifiesto ]",
      chapters: [
        { lead: "El marcador", rest: "es más real que la foto." },
        { lead: "El ranking", rest: "es más real que el post." },
        { lead: "La resenha", rest: "es la mejor parte de cada juego." },
      ],
      closing: "PINWIN es el álbum de estampas de la vida real. Donde el picadito de los miércoles tiene ranking oficial, donde el truco del asado tiene narrador con IA, y donde cada juego entre amigos se vuelve historia, rivalidad y resenha.",
    },
    waitlist: {
      overline: "[ Reserva tu handle ]", headA: "Registra tu", headB: "primera victoria.",
      subPre: "Reclama tu ", subLink: "pinwin.social/@handle", subPost: " antes de que lo haga alguien de tu squad. Únete a la lista y sé el primero en el lanzamiento.",
      handleLabel: "Handle", handlePlaceholder: "tunombre",
      emailLabel: "Email", emailPlaceholder: "tu@email.com",
      submit: "Reservar mi handle", footnote: "100% gratis · sin ads · sin spam",
      available: "Disponible — agárralo.", taken: "Ya está reservado. Prueba otro.", invalid: "3–20 caracteres · a-z, 0-9, _ solamente.",
      successTitleSuffix: "asegurado",
      successPre: "Estás en la lista de espera. Te avisamos por email en cuanto PINWIN se lance.", successMid: "",
      toastSuccess: (h, p) => `@${h} es tuyo!`,
      toastTaken: "Ese handle ya está reservado. Prueba otro.",
      toastInvalidHandle: "El handle debe tener 3–20 caracteres: letras, números, guion bajo.",
      toastInvalidEmail: "Ingresa un email válido.", toastGeneric: "Algo salió mal. Inténtalo de nuevo.",
    },
    footer: {
      cta: "Reserva tu @handle →", tagPre: "Si pasó,", tagStrong: "PinWin.",
      cols: [
        { title: "Producto", links: ["Squads", "Vault", "Resenha Engine", "Deportes"] },
        { title: "Empresa", links: ["Manifiesto", "Empleos", "Prensa", "Contacto"] },
        { title: "Legal", links: ["Privacidad", "Términos", "Cookies"] },
      ],
      copyright: "Domina el Marcador. Pin the Win.",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("pw_lang") || "en");
  useEffect(() => {
    localStorage.setItem("pw_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
